import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Settings, Home, LogOut, FileText, Users, Upload, ClipboardList, Pill, RefreshCw, History, Edit, ShieldCheck, Eye, AlertTriangle, Video, Beaker } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SandboxBanner from "@/components/SandboxBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openReports, setOpenReports] = useState(0);
  const { token, user, logout, isAdmin, isEditor, isDemoAdmin, canAdminUI, userRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, reportsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/error-reports?status=open`).catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setOpenReports(reportsRes.data.length);
      } catch (error) {
        if (error.response?.status === 401) {
          logout();
          navigate("/login");
        }
        toast.error("Failed to load stats");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token, logout, navigate]);

  const handleReseed = async () => {
    if (!window.confirm("This will clear and reseed all data. Continue?")) return;
    
    try {
      await axios.post(`${API}/admin/reseed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Database reseeded successfully!");
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      toast.error("Failed to reseed database");
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-heading font-bold flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Admin Dashboard
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground flex items-center gap-2">
              Logged in as: <strong>{user?.email}</strong>
              <Badge variant={userRole === 'admin' ? 'default' : userRole === 'editor' ? 'outline' : 'secondary'}
                     className={userRole === 'editor' ? 'border-blue-500 text-blue-500' : ''}>
                {userRole === 'admin' ? <ShieldCheck className="w-3 h-3 mr-1" /> : 
                 userRole === 'editor' ? <Edit className="w-3 h-3 mr-1" /> : 
                 <Eye className="w-3 h-3 mr-1" />}
                {userRole}
              </Badge>
            </span>
            <Link to="/"><Button variant="outline" size="sm"><Home className="w-4 h-4 mr-2" />App</Button></Link>
            <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <SandboxBanner />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold">{stats?.protocols || 0}</div>
              <div className="text-sm text-muted-foreground">Protocols</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold">{stats?.medications || 0}</div>
              <div className="text-sm text-muted-foreground">Medications</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold">{stats?.procedures || 0}</div>
              <div className="text-sm text-muted-foreground">Procedures</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold">{stats?.categories || 0}</div>
              <div className="text-sm text-muted-foreground">Categories</div>
            </CardContent>
          </Card>
        </div>

        {stats?.source_document && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Source Document
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p><strong>Name:</strong> {stats.source_document.name}</p>
              <p><strong>Version:</strong> {stats.source_document.version}</p>
              <p><strong>File:</strong> {stats.source_document.filename}</p>
              {stats.source_document.uploaded_at && (
                <p><strong>Uploaded:</strong> {new Date(stats.source_document.uploaded_at).toLocaleString()}</p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {isAdmin && (
            <Link to="/admin/users">
              <Card className="cursor-pointer hover:border-primary transition-colors h-full">
                <CardContent className="pt-6 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="font-medium">Manage Users</div>
                </CardContent>
              </Card>
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin/upload">
              <Card className="cursor-pointer hover:border-primary transition-colors h-full">
                <CardContent className="pt-6 text-center">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="font-medium">Upload Document</div>
                </CardContent>
              </Card>
            </Link>
          )}
          <Link to="/admin/protocols">
            <Card className="cursor-pointer hover:border-primary transition-colors h-full">
              <CardContent className="pt-6 text-center">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 text-primary" />
                <div className="font-medium">Manage Protocols</div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/medications">
            <Card className="cursor-pointer hover:border-primary transition-colors h-full">
              <CardContent className="pt-6 text-center">
                <Pill className="w-8 h-8 mx-auto mb-2 text-primary" />
                <div className="font-medium">Manage Medications</div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/procedures">
            <Card className="cursor-pointer hover:border-primary transition-colors h-full">
              <CardContent className="pt-6 text-center">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 text-primary" />
                <div className="font-medium">Manage Procedures</div>
              </CardContent>
            </Card>
          </Link>
          {isAdmin && (
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={handleReseed}>
              <CardContent className="pt-6 text-center">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 text-primary" />
                <div className="font-medium">Reseed Database</div>
              </CardContent>
            </Card>
          )}
          <Link to="/admin/error-reports">
            <Card className={`cursor-pointer hover:border-primary transition-colors h-full ${openReports > 0 ? 'border-amber-500/50' : ''}`}>
              <CardContent className="pt-6 text-center relative">
                <AlertTriangle className={`w-8 h-8 mx-auto mb-2 ${openReports > 0 ? 'text-amber-500' : 'text-primary'}`} />
                <div className="font-medium">Review Errors</div>
                {openReports > 0 && (
                  <Badge className="absolute top-3 right-3 bg-amber-500 text-white text-xs">{openReports}</Badge>
                )}
              </CardContent>
            </Card>
          </Link>
          {isAdmin && (
            <Link to="/admin/videos" data-testid="admin-videos-link">
              <Card className="cursor-pointer hover:border-primary transition-colors h-full">
                <CardContent className="pt-6 text-center">
                  <Video className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="font-medium">Video Library</div>
                </CardContent>
              </Card>
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin/concentrations" data-testid="admin-concentrations-link">
              <Card className="cursor-pointer hover:border-primary transition-colors h-full">
                <CardContent className="pt-6 text-center">
                  <Beaker className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="font-medium">Drug Concentrations</div>
                </CardContent>
              </Card>
            </Link>
          )}
          {canAdminUI && (
            <Link to="/admin/audit" data-testid="admin-audit-link">
              <Card className="cursor-pointer hover:border-primary transition-colors h-full">
                <CardContent className="pt-6 text-center">
                  <History className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="font-medium">Audit & Revert</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Concentration history</div>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Recent Changes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_changes?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_changes.map((change, i) => (
                  <div key={i} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <Badge variant="outline" className="mr-2">{change.action}</Badge>
                      <span className="font-medium">{change.entity_name}</span>
                      <span className="text-sm text-muted-foreground ml-2">({change.entity_type})</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(change.timestamp).toLocaleString()} by {change.admin_username}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No recent changes</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
